import { useEffect, useMemo, useRef, useState } from 'react';
import { getSchema } from '@tiptap/core';
import { useEditor } from '@tiptap/react';
import { EditorState } from '@tiptap/pm/state';
import { emptyTiptapDoc, textFromTiptap } from '@mts/shared/contentDocument';
import { createContentEditorPolicy } from './contentEditorPolicy.js';
import { createContentEditorClipboard } from './contentEditorClipboard.js';
import { prepareContentEditorDocument } from './contentEditorDocument.js';

const EMPTY_EXTENSIONS = [];
export const CONTENT_EDITOR_HOST_LOAD_META = 'mtsContentEditorHostLoad';

/** Shared authoring lifecycle. Hosts supply schemas/adapters; no app persistence lives here. */
export function useContentEditorKernel({ profile, value, tenantId, extensions = EMPTY_EXTENSIONS, onChange, onEditorReady, onSelectionChange, editorProps = {} }) {
  const current = useRef({ profile, value, tenantId, onChange, onEditorReady, onSelectionChange });
  current.current = { profile, value, tenantId, onChange, onEditorReady, onSelectionChange };
  const documentSource = useRef(value);
  const controlledSource = useRef(value);
  if (controlledSource.current !== value) {
    controlledSource.current = value;
    documentSource.current = value;
  }
  const serialize = (active) => {
    const source = documentSource.current;
    const doc = active.getJSON();
    const envelope = source && typeof source === 'object' && 'schemaVersion' in source;
    const root = envelope ? source.doc : source;
    if (root?.attrs) doc.attrs = { ...root.attrs, ...doc.attrs };
    return envelope ? { ...source, doc } : doc;
  };
  const recoveryRequired = useRef(true);
  const loading = useRef(false);
  const [rejectedIssues, setRejectedIssues] = useState([]);
  const policy = useMemo(() => createContentEditorPolicy({ getProfile: () => ({ ...current.current.profile, readOnly: current.current.profile.readOnly || recoveryRequired.current }), isControlledLoad: () => loading.current, onRejected: setRejectedIssues }), []);
  const clipboard = useMemo(() => createContentEditorClipboard(() => ({ tenantId: current.current.tenantId, onRejected: setRejectedIssues })), []);
  const configuredExtensions = useMemo(() => [...extensions, policy, clipboard], [extensions, policy, clipboard]);
  const inspection = useMemo(() => {
    const result = prepareContentEditorDocument(value, profile);
    if (!result.valid) return result;
    try {
      const schema = getSchema(configuredExtensions);
      schema.nodeFromJSON(result.document).check();
      const visit = (node) => {
        if (!schema.nodes[node.type]) throw new Error(`Unavailable editor node: ${node.type}`);
        for (const mark of node.marks || []) if (!schema.marks[mark.type]) throw new Error(`Unavailable editor mark: ${mark.type}`);
        for (const child of node.content || []) visit(child);
      };
      visit(result.document);
      return result;
    } catch (error) {
      return { ...result, valid: false, issues: [...result.issues, { code: 'unavailable-schema', severity: 'error', message: error.message }] };
    }
  }, [value, profile, configuredExtensions]);
  recoveryRequired.current = !inspection.valid;
  const lastControlledLoad = useRef(null);
  const lastRecoveryLoad = useRef(null);
  const lastTenant = useRef(tenantId);
  const editor = useEditor({
    extensions: configuredExtensions,
    content: inspection.valid ? inspection.document : emptyTiptapDoc(),
    editable: inspection.valid && !profile.readOnly,
    immediatelyRender: false,
    editorProps,
    onUpdate: ({ editor: active }) => {
      if (loading.current || recoveryRequired.current) return;
      const host = current.current;
      const doc = active.getJSON();
      // Tiptap serializes declared schema attributes only. Preserve source metadata.
      const output = serialize(active);
      host.onChange?.(output, { html: active.getHTML(), text: textFromTiptap(doc) });
      setRejectedIssues([]);
    },
    onSelectionUpdate: ({ editor: active }) => current.current.onSelectionChange?.(active.state.selection, active),
    onTransaction: ({ editor: active }) => current.current.onSelectionChange?.(active.state.selection, active),
  }, [configuredExtensions]);
  useEffect(() => {
    if (!editor) return;
    current.current.onEditorReady?.(editor);
    return () => current.current.onEditorReady?.(null);
  }, [editor]);
  useEffect(() => {
    if (!editor) return;
    const tenantChanged = lastTenant.current !== tenantId;
    lastTenant.current = tenantId;
    const resetState = () => {
      editor.view.updateState(EditorState.create({ schema: editor.schema, doc: editor.state.doc, plugins: editor.state.plugins }));
      editor.view.dispatch(editor.state.tr.setMeta('addToHistory', false).setMeta(CONTENT_EDITOR_HOST_LOAD_META, true));
    };
    editor.setEditable(inspection.valid && !profile.readOnly, false);
    if (!inspection.valid) {
      if (tenantChanged || lastRecoveryLoad.current?.editor !== editor || lastRecoveryLoad.current.value !== value) resetState();
      lastRecoveryLoad.current = { editor, value };
      return;
    }
    lastRecoveryLoad.current = null;
    if (!tenantChanged && lastControlledLoad.current?.editor === editor && lastControlledLoad.current.value === value) return;
    lastControlledLoad.current = { editor, value };
    const incoming = inspection.document;
    const actual = editor.getJSON();
    // Root metadata is retained in the host document, outside the editor schema.
    if (!tenantChanged && JSON.stringify({ ...actual, attrs: undefined }) === JSON.stringify({ ...incoming, attrs: undefined })) return;
    loading.current = true;
    try {
      editor.commands.setContent(incoming, { emitUpdate: false });
      // Host loads start a new authoring history; echoed author changes do not.
      resetState();
    }
    finally { loading.current = false; }
  }, [editor, inspection, profile.readOnly, value, tenantId]);
  const replaceDocument = (candidate, options = {}) => {
    if (!editor?.isEditable || recoveryRequired.current) return false;
    const result = prepareContentEditorDocument(candidate, current.current.profile);
    if (!result.valid) { setRejectedIssues(result.issues); return false; }
    try {
      editor.schema.nodeFromJSON(result.document).check();
      const previousSource = documentSource.current;
      const original = editor.state.doc;
      documentSource.current = candidate?.schemaVersion ? candidate : previousSource?.schemaVersion ? { ...previousSource, doc: result.document } : result.document;
      try {
        editor.commands.setContent(result.document, { emitUpdate: options.emitUpdate !== false });
        if (editor.state.doc === original) { documentSource.current = previousSource; return false; }
        return true;
      } catch (error) { documentSource.current = previousSource; throw error; }
    } catch (error) { setRejectedIssues([{ code: 'replacement-schema', severity: 'error', message: error.message }]); return false; }
  };
  return { editor, getDocument: () => inspection.valid && editor ? serialize(editor) : value, replaceDocument, recovery: inspection.valid ? null : inspection, issues: inspection.valid ? rejectedIssues : inspection.issues };
}
